import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Button, Card } from '@/shared/ui';

const REDIRECT_DELAY_MS = 5000;

/**
 * Shown after PayPal redirects back on successful subscription approval.
 * The subscription isn't active yet — PayPal fires a webhook to activate it.
 * We show a "processing" message and auto-redirect to the dashboard.
 */
export default function SubscriptionSuccessPage(): React.ReactElement {
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/dashboard', { replace: true });
    }, REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <Card className="flex max-w-sm flex-col items-center gap-5 py-10 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100">
          <CheckCircle2 size={36} className="text-secondary-600" aria-hidden="true" />
        </div>

        <div className="space-y-2">
          <h1 className="text-xl font-bold text-slate-900">You're all set!</h1>
          <p className="text-sm text-slate-600">
            Your subscription is being activated. This usually takes just a moment.
            You'll be redirected to your dashboard automatically.
          </p>
        </div>

        <div className="h-1 w-full overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full bg-secondary-500 rounded-full"
            style={{
              animation: `expand ${REDIRECT_DELAY_MS}ms linear forwards`,
            }}
          />
        </div>

        <Button variant="ghost" onClick={() => navigate('/dashboard', { replace: true })}>
          Go to dashboard now
        </Button>
      </Card>

      <style>{`
        @keyframes expand {
          from { width: 0% }
          to   { width: 100% }
        }
      `}</style>
    </div>
  );
}
